import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Search, Calendar, FileText, Clock, MapPin } from "lucide-react";
import { format } from "date-fns";

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [results, setResults] = useState({ posts: [], events: [] });
  const [loading, setLoading] = useState(true);

  const fetchResults = useCallback(async () => {
    if (!query) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
      setResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  const totalResults = results.posts.length + results.events.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white border-b">
        <div className="container-custom py-8">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-primary-900 mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Search className="w-8 h-8 text-primary-900" />
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
                Search Results
              </h1>
              <p className="text-gray-600">
                {loading ? "Searching..." : `${totalResults} result${totalResults !== 1 ? 's' : ''} for "${query}"`}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </Card>
              ))}
            </div>
          ) : totalResults === 0 ? (
            <Card className="p-12 text-center">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-500 mb-6">
                We couldn't find anything matching "{query}". Try different keywords.
              </p>
              <Link to="/" className="text-primary-900 hover:text-primary-700 font-medium">
                ← Return to Home
              </Link>
            </Card>
          ) : (
            <div className="space-y-8">
              {/* Posts Results */}
              {results.posts.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-900" />
                    Posts ({results.posts.length})
                  </h2>
                  <div className="space-y-4">
                    {results.posts.map((post) => (
                      <Card key={post.post_id} className="card-interactive" data-testid={`search-post-${post.post_id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              post.category === 'news' ? 'bg-blue-100 text-blue-700' :
                              post.category === 'announcement' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {post.category}
                            </span>
                            {post.committee && (
                              <span className="text-xs text-gray-500">{post.committee}</span>
                            )}
                          </div>
                          <Link to={`/post/${post.post_id}`}>
                            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2 hover:text-primary-900 transition-colors">
                              {post.title}
                            </h3>
                          </Link>
                          <p className="text-gray-600 line-clamp-2 mb-3">{post.content}</p>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Clock className="w-4 h-4" />
                            {formatDate(post.created_at)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Events Results */}
              {results.events.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-secondary-600" />
                    Events ({results.events.length})
                  </h2>
                  <div className="space-y-4">
                    {results.events.map((event) => (
                      <Card key={event.event_id} className="card-interactive" data-testid={`search-event-${event.event_id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-lg bg-secondary-100 flex flex-col items-center justify-center flex-shrink-0">
                              <span className="text-lg font-bold text-secondary-700">
                                {format(new Date(event.date), "d")}
                              </span>
                              <span className="text-xs text-secondary-600">
                                {format(new Date(event.date), "MMM")}
                              </span>
                            </div>
                            <div>
                              <Link to="/events">
                                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-1 hover:text-primary-900 transition-colors">
                                  {event.title}
                                </h3>
                              </Link>
                              <p className="text-gray-600 line-clamp-2 mb-2">{event.description}</p>
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <MapPin className="w-4 h-4" />
                                {event.location}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SearchResults;
